import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import Typed from "typed.js";
import {
  BoardPlaceholder,
  CreditsSheet,
  DEFAULT_ASSIGNMENT,
  HarborSheet,
  Keyboard,
  PaperClip,
  Pencil,
  QuickStartSheet,
  ReferenceSheet,
  ReleaseNotesSheet,
  Sticker,
  Polaroid,
  StickyNote,
  StudySheet,
  WaitlistTag,
  clampFrontLift,
  layoutDesk,
  slotTransform,
  type SheetId,
  type SlotName,
} from "./desk";

const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "0x4AAAAAAEJvE4Aq7awI8x9y";
/** The Aliyun copy (docs/deploy-aliyun.md): its forms send people to the main site. */
const CHINA_MIRROR = import.meta.env.VITE_CHINA_MIRROR === "1";
const WAITLIST_API = "/api/waitlist";
const WAITLIST_SUCCESS_MESSAGE = "You're on the list. We'll be in touch.";
/** Remembered so the feature board stays unlocked on the next visit. */
const SUBSCRIBED_STORAGE_KEY = "typora-v2-waitlist";
/** Below this the desk gives way to the single-sheet layout. */
const DESK_MEDIA = "(min-width: 1024px)";
const KIIKU_EMBED_BASE =
  import.meta.env.VITE_KIIKU_EMBED_BASE ??
  (import.meta.env.DEV ? "http://localhost:5173" : "https://typora.kiiku.io");
const KIIKU_EMBED_KEY =
  import.meta.env.VITE_KIIKU_EMBED_KEY ?? "typora_features_v1";
const KIIKU_HOST_ORIGIN = import.meta.env.VITE_KIIKU_HOST_ORIGIN;
const TITLE = "# A New Chapter";
const TITLE_TEXT = "A New Chapter";
const BODY_1 =
  "Typora introduced a new paradigm for markdown: removing the distraction of the markup itself. It gave writers a live, clean canvas where formatting disappears the moment it's typed.";
const BODY_2_HTML =
  "Now, <strong>Version 2.0</strong> is coming. It is a completely rebuilt experience that retains the seamless, distraction-free markdown you love, while introducing powerful new tools for modern writers.";
const CLOSING_LINE =
  "The interface remains as silent as ever. The capabilities underneath have multiplied.";

/** The slogan types itself on the desk; the last word is highlighted after. */
const SLOGAN_HEAD = "A simple yet powerful markdown editor, ";
const SLOGAN_MARK = "refined";
const SLOGAN = SLOGAN_HEAD + SLOGAN_MARK;
const SLOGAN_START_DELAY = 520;

/** ms/char — lower is faster. No startDelay. */
const TYPE_SPEED = 4;
/** Marks the element currently being typed; CSS draws a steady caret after its text. */
const CARET_CLASS = "is-typing";
/** Left on the last element once everything is typed, so the caret rests and blinks. */
const CARET_RESTING_CLASS = "is-caret-resting";
/** Sweeps the highlighter across the last word once the slogan is finished. */
const MARK_CLASS = "is-marked";

/**
 * How long to wait before the next keystroke. A hand at a keyboard is not a
 * metronome: words run together, the space before one costs a beat, a comma
 * buys a breath, and now and then a finger hesitates.
 */
function keystrokeDelay(typed: string, next: string | undefined): number {
  if (typed === ",") return 260 + Math.random() * 130;
  if (typed === " ") return 120 + Math.random() * 90;
  if (next === " ") return 90 + Math.random() * 70;
  const base = 58 + Math.random() * 62;
  return Math.random() < 0.08 ? base + 170 : base;
}

const SHEET_ORDER: SheetId[] = [
  "letter",
  "features",
  "quickstart",
  "reference",
  "release",
  "harbor",
  "credits",
  "study",
];

const SHEET_LABELS: Record<SheetId, string> = {
  letter: "A New Chapter, wait list",
  features: "Feature requests",
  quickstart: "Quick start",
  reference: "Markdown reference",
  release: "What's new in 1.14",
  harbor: "Harbor, early",
  credits: "Acknowledgement",
  study: "Gradient descent, notes",
};

type WaitlistStatus = "idle" | "loading" | "success" | "error";

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  theme?: "light" | "dark" | "auto";
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
  getResponse: (widgetId?: string) => string | undefined;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    onloadTurnstileCallback?: () => void;
  }
}

/**
 * api.js is loaded with `render=explicit`, so the widget has to be created by
 * hand: implicit rendering scans the DOM once on script load, which happens
 * before React mounts the container, so it would silently never run.
 */
function whenTurnstileReady(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  return new Promise((resolve) => {
    const previous = window.onloadTurnstileCallback;
    window.onloadTurnstileCallback = () => {
      previous?.();
      resolve(window.turnstile!);
    };
  });
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
}

function readStoredSubscription(): boolean {
  try {
    return window.localStorage.getItem(SUBSCRIBED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function storeSubscription() {
  try {
    window.localStorage.setItem(SUBSCRIBED_STORAGE_KEY, "1");
  } catch {
    // Private mode or blocked storage: the unlock just won't survive a reload.
  }
}

function kiikuEmbedUrl(): { url: string; origin: string } {
  const origin = new URL(KIIKU_EMBED_BASE).origin;
  const url = new URL("/embed.html", KIIKU_EMBED_BASE);
  url.searchParams.set("key", KIIKU_EMBED_KEY);
  url.searchParams.set("hostOrigin", KIIKU_HOST_ORIGIN ?? window.location.origin);
  url.searchParams.set("style", "minimal");
  return { url: url.toString(), origin };
}

/* ------------------------------------------------------------------ */
/* Shared pieces                                                       */
/* ------------------------------------------------------------------ */

type WaitlistProps = {
  idPrefix: string;
  email: string;
  status: WaitlistStatus;
  message: string;
  onEmailChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
};

function WaitlistForm({ idPrefix, email, status, message, onEmailChange, onSubmit }: WaitlistProps) {
  return (
    <form className="waitlist-form" onSubmit={onSubmit} noValidate>
      <div className="form-row">
        <div className="email-field">
          <span className="email-icon" aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
          </span>
          <input
            id={`${idPrefix}-email`}
            type="email"
            className="email-input"
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder="Enter your email address..."
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            disabled={status === "loading"}
            aria-label="Email address"
            required
          />
        </div>
        <button type="submit" className="submit-btn" disabled={status === "loading"}>
          {status === "loading" ? "Subscribing…" : "Subscribe"}
        </button>
      </div>
      {message ? (
        <p className="form-message is-error" role="status">
          {message}
        </p>
      ) : null}
    </form>
  );
}

function MainSiteLink() {
  return (
    <a className="submit-btn main-site-link" href="https://v2.typora.io">
      Go to v2.typora.io
    </a>
  );
}

type LetterRefs = {
  title: RefObject<HTMLSpanElement | null>;
  body1: RefObject<HTMLParagraphElement | null>;
  body2: RefObject<HTMLParagraphElement | null>;
  closing: RefObject<HTMLParagraphElement | null>;
  waitlist: RefObject<HTMLDivElement | null>;
};

/** The hero measures itself (for the hover clearance) and types its slogan. */
type HeroRefs = {
  root: RefObject<HTMLDivElement | null>;
  head: RefObject<HTMLSpanElement | null>;
  mark: RefObject<HTMLSpanElement | null>;
};

/**
 * How much of the letter is shown. A short desk drops the opening and closing
 * lines, then the prose altogether, so the wait-list form is always on screen.
 */
const LETTER_DENSITIES = ["full", "brief", "bare"] as const;
type LetterDensity = (typeof LETTER_DENSITIES)[number];

type LetterProps = WaitlistProps & {
  refs: LetterRefs;
  featureBoardOpen: boolean;
  density?: LetterDensity;
  onRequestFeature: (event: MouseEvent<HTMLButtonElement>) => void;
};

function LetterContent({
  refs,
  featureBoardOpen,
  density = "full",
  onRequestFeature,
  ...form
}: LetterProps) {
  const { status, message } = form;
  return (
    <div className={`paper-content${density === "full" ? "" : ` is-${density}`}`}>
      <h1 className="doc-header is-parsed" aria-label={TITLE}>
        <span className="md-hash" aria-hidden="true">
          #
        </span>
        <span className="doc-title">
          <span className="doc-title-ghost" aria-hidden="true">
            {TITLE_TEXT}
          </span>
          <span className="doc-title-live">
            <span ref={refs.title} className="doc-title-typed" />
          </span>
        </span>
      </h1>

      <div className="doc-body">
        <div className="doc-body-ghost" aria-hidden="true">
          <p>{BODY_1}</p>
          <p dangerouslySetInnerHTML={{ __html: BODY_2_HTML }} />
          <p>{CLOSING_LINE}</p>
        </div>
        <div className="doc-body-live">
          <p ref={refs.body1} />
          <p ref={refs.body2} />
          <p ref={refs.closing} />
        </div>
      </div>

      <div className="paper-divider" role="separator" aria-hidden="true">
        <span className="paper-divider-dots">···</span>
      </div>

      <div ref={refs.waitlist} className="waitlist-section">
        {CHINA_MIRROR ? (
          <div className="waitlist-veiled">
            <MainSiteLink />
            <div className="waitlist-veil" aria-hidden="true" inert>
              <span className="waitlist-label">Join the wait list</span>
              <WaitlistForm {...form} />
            </div>
          </div>
        ) : status === "success" ? (
          <div className="waitlist-done" role="status">
            <p className="waitlist-done-line">
              <span className="waitlist-check" aria-hidden="true">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path className="waitlist-check-mark" d="m4 12.6 5.2 5.2L20 6.8" />
                </svg>
              </span>
              {message}
            </p>
            <button
              type="button"
              className="feature-request-link"
              aria-expanded={featureBoardOpen}
              aria-controls="typora-feature-board"
              onClick={onRequestFeature}
            >
              Request a feature <span aria-hidden="true">↗</span>
            </button>
          </div>
        ) : (
          <>
            <label className="waitlist-label" htmlFor="waitlist-email">
              Join the wait list
            </label>
            <WaitlistForm {...form} />
          </>
        )}
      </div>
    </div>
  );
}

type FeatureBoardFrameProps = {
  /** Interactive only while it is the sheet being read (or the open overlay). */
  active: boolean;
  onRequestClose: () => void;
};

/** The Kiiku feature board, embedded. */
function FeatureBoardFrame({ active, onRequestClose }: FeatureBoardFrameProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const embed = kiikuEmbedUrl();

  const syncViewport = useCallback(() => {
    const frame = frameRef.current;
    if (!frame?.contentWindow) return;
    // The frame is sized to the visible part of the sheet, so nothing of it is
    // hidden above; the board only needs to know it has a stable viewport.
    frame.contentWindow.postMessage(
      { type: "kiiku:host-viewport", topOffset: 0, rightInset: 0 },
      embed.origin,
    );
  }, [embed.origin]);

  useEffect(() => {
    function receive(event: MessageEvent) {
      if (event.origin !== embed.origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;
      const data = event.data as { type?: string } | null;
      switch (data?.type) {
        case "kiiku:ready":
        case "kiiku:resize":
        case "kiiku:navigate":
          window.requestAnimationFrame(syncViewport);
          break;
        case "kiiku:request-close":
          onRequestClose();
          break;
      }
    }
    window.addEventListener("message", receive);
    window.addEventListener("resize", syncViewport);
    return () => {
      window.removeEventListener("message", receive);
      window.removeEventListener("resize", syncViewport);
    };
  }, [embed.origin, onRequestClose, syncViewport]);

  return (
    <iframe
      ref={frameRef}
      className="feature-board-frame"
      src={embed.url}
      title="Typora feature requests"
      loading="eager"
      referrerPolicy="strict-origin-when-cross-origin"
      sandbox="allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
      inert={!active}
      onLoad={syncViewport}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Desk                                                                */
/* ------------------------------------------------------------------ */

type SheetProps = {
  id: SheetId;
  isFront: boolean;
  transform: string;
  zIndex: number;
  className?: string;
  /** Drawn in the slot but outside the paper, which clips its own contents. */
  marker?: ReactNode;
  onPick: (id: SheetId) => void;
  children: ReactNode;
};

/**
 * One sheet on the desk. Aside sheets are inert (clicks land on the slot and
 * bring the sheet forward, nothing inside can be focused or, for the board,
 * swallow the click); the front sheet is live.
 */
function Sheet({
  id,
  isFront,
  transform,
  zIndex,
  className,
  marker,
  onPick,
  children,
}: SheetProps) {
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (isFront || event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onPick(id);
    }
  }
  return (
    <div
      className={`slot ${isFront ? "is-front" : "is-aside"}`}
      style={{ transform, zIndex }}
      tabIndex={isFront ? -1 : 0}
      role={isFront ? undefined : "button"}
      aria-label={isFront ? undefined : `Read: ${SHEET_LABELS[id]}`}
      onClick={() => {
        if (!isFront) onPick(id);
      }}
      onKeyDown={onKeyDown}
    >
      <div className={`sheet${className ? ` ${className}` : ""}`} inert={!isFront}>
        {children}
      </div>
      {marker}
    </div>
  );
}

/**
 * Distance from the top of the sheet to the top of `el`. Measured through
 * offsetTop rather than a client rect, because a sheet moving between slots is
 * mid-transform and would otherwise be measured at whatever scale it is
 * passing through.
 */
function offsetWithinSheet(el: HTMLElement): number {
  let top = el.offsetTop;
  let parent = el.offsetParent as HTMLElement | null;
  while (parent && !parent.classList.contains("sheet")) {
    top += parent.offsetTop;
    parent = parent.offsetParent as HTMLElement | null;
  }
  return top;
}

type StageProps = {
  letterRefs: LetterRefs;
  form: WaitlistProps;
  subscribed: boolean;
};

type DeskProps = StageProps & { heroRefs: HeroRefs };

function Desk({ letterRefs, heroRefs, form, subscribed }: DeskProps) {
  const stageRef = useRef<HTMLElement>(null);
  const [size, setSize] = useState({ width: 1440, height: 900 });
  const [assignment, setAssignment] = useState(DEFAULT_ASSIGNMENT);
  const [frontLift, setFrontLift] = useState(56);
  const [density, setDensity] = useState<LetterDensity>("full");
  /** Centre of the wait-list field inside the sheet; null once subscribed. */
  const [fieldCentre, setFieldCentre] = useState<number | null>(null);
  /** Where the form ends inside the sheet, per density, once measured. */
  const formBottom = useRef<Partial<Record<LetterDensity, number>>>({});
  /** Measured, because the title block grows with the viewport and the font. */
  const [heroHeight, setHeroHeight] = useState(125);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const hero = heroRefs.root.current;
    if (!stage) return;
    const measure = () => {
      setSize({ width: stage.clientWidth, height: stage.clientHeight });
      if (hero) setHeroHeight(hero.offsetHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    if (hero) observer.observe(hero);
    return () => observer.disconnect();
  }, [heroRefs.root]);

  const layout = useMemo(
    () => layoutDesk(size.width, size.height),
    [size.width, size.height],
  );

  // A subscribed letter is a different height, so the measurements start over.
  useLayoutEffect(() => {
    formBottom.current = {};
  }, [form.status]);

  // Trim the letter until the form fits the visible part of the sheet, then
  // lift the sheet on hover just far enough to clear the bottom edge.
  useLayoutEffect(() => {
    const waitlist = letterRefs.waitlist.current;
    if (!waitlist) return;
    const bottom = offsetWithinSheet(waitlist) + waitlist.offsetHeight;
    formBottom.current[density] = bottom;

    // Keep the fullest letter whose form still ends above the desk edge. A
    // density nobody has measured yet is worth trying: one pass settles it.
    const visible = size.height - layout.frontTop;
    const fits = (candidate: LetterDensity) =>
      (formBottom.current[candidate] ?? 0) <= visible - 20;
    const wanted = LETTER_DENSITIES.find(fits) ?? "bare";
    if (wanted !== density) {
      setDensity(wanted);
      return;
    }

    const lift = Math.min(320, Math.max(56, bottom - visible + 32));
    // …but never so far that the sheet crowds the title and slogan.
    setFrontLift(clampFrontLift(lift, layout, heroHeight));

    const field = waitlist.querySelector("input");
    setFieldCentre(field ? offsetWithinSheet(field) + field.offsetHeight / 2 : null);
  }, [
    density,
    heroHeight,
    layout,
    layout.frontTop,
    letterRefs.waitlist,
    size.height,
    form.status,
  ]);

  const slotOf = useCallback(
    (id: SheetId): SlotName =>
      (Object.keys(assignment) as SlotName[]).find((slot) => assignment[slot] === id) ??
      "front",
    [assignment],
  );

  /** Bring a sheet to the reading spot; the one that was there takes its place. */
  const bring = useCallback(
    (id: SheetId) => {
      setAssignment((current) => {
        const from = (Object.keys(current) as SlotName[]).find(
          (slot) => current[slot] === id,
        );
        if (!from || from === "front") return current;
        return { ...current, [from]: current.front, front: id };
      });
    },
    [],
  );

  const bringLetter = useCallback(() => bring("letter"), [bring]);

  useEffect(() => {
    if (assignment.front !== "features") return;
    function closeOnEscape(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") bringLetter();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [assignment.front, bringLetter]);

  /** What the tab does: read the letter, then type in the field it points at. */
  const focusWaitlist = useCallback(() => {
    bringLetter();
    // The letter may still be arriving, and is inert until it does.
    window.requestAnimationFrame(() => {
      letterRefs.waitlist.current?.querySelector<HTMLElement>("a, input")?.focus();
    });
  }, [bringLetter, letterRefs.waitlist]);

  const featuresFront = assignment.front === "features";
  const locked = !subscribed;

  const contents: Record<SheetId, ReactNode> = {
    letter: (
      <LetterContent
        refs={letterRefs}
        {...form}
        density={density}
        featureBoardOpen={featuresFront}
        onRequestFeature={(event) => {
          event.stopPropagation();
          bring("features");
        }}
      />
    ),
    features: (
      <>
        {locked ? (
          <BoardPlaceholder />
        ) : (
          <FeatureBoardFrame active={featuresFront} onRequestClose={bringLetter} />
        )}
        {locked ? (
          <>
            <div className="lock-veil" aria-hidden="true" />
            <div className="lock-card">
              <span className="lock-icon" aria-hidden="true">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="4" y="10.5" width="16" height="10" rx="2" />
                  <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
                </svg>
              </span>
              {CHINA_MIRROR ? (
                <>
                  <h2 className="lock-title">Continue on v2.typora.io</h2>
                  <p className="lock-text">
                    The wait list and the feature board are on the main site.
                  </p>
                  <MainSiteLink />
                </>
              ) : (
                <>
                  <h2 className="lock-title">Subscribe to unlock</h2>
                  <p className="lock-text">
                    The feature board opens the moment you're on the wait list.
                  </p>
                  <WaitlistForm {...form} idPrefix="lock" />
                </>
              )}
            </div>
          </>
        ) : null}
      </>
    ),
    quickstart: <QuickStartSheet />,
    reference: <ReferenceSheet />,
    release: <ReleaseNotesSheet />,
    harbor: <HarborSheet />,
    credits: <CreditsSheet />,
    study: <StudySheet />,
  };

  const stageStyle = {
    "--title-top": `${layout.titleTop}px`,
    "--front-top": `${layout.frontTop}px`,
    "--front-lift": `${frontLift}px`,
  } as CSSProperties;

  return (
    <main
      ref={stageRef}
      className={`stage stage-desk${subscribed ? " is-subscribed" : ""}`}
      style={stageStyle}
    >
      <header className="site-header desk-header">
        <a
          className="current-version"
          href="https://typora.io"
          target="_blank"
          rel="noreferrer"
        >
          Current Version <span aria-hidden="true">↗</span>
        </a>
      </header>

      <Sticker {...layout.stationery.pin} onActivate={focusWaitlist} />

      <div ref={heroRefs.root} className="hero" style={{ top: layout.titleTop }}>
        <h1 className="hero-title">Typora 2.0</h1>
        {/* The ghost holds the line's full width so the typed text, laid over it
            from the left, never shifts the centred layout under itself. */}
        <p className="hero-slogan">
          <span className="hero-slogan-ghost" aria-hidden="true">
            {SLOGAN_HEAD}
            <span className="hero-mark">{SLOGAN_MARK}</span>
          </span>
          <span className="hero-slogan-live" aria-hidden="true">
            <span ref={heroRefs.head} />
            <span ref={heroRefs.mark} className="hero-mark" />
          </span>
          <span className="visually-hidden">{SLOGAN}</span>
        </p>
      </div>

      {SHEET_ORDER.map((id) => {
        const slot = slotOf(id);
        const geometry = layout.slots[slot];
        const isFront = slot === "front";
        const className =
          id === "letter"
            ? "letter"
            : id === "features"
              ? `features${locked ? " is-locked" : ""}`
              : undefined;
        const marker =
          id === "letter" && isFront && fieldCentre != null ? (
            <WaitlistTag top={fieldCentre} onActivate={focusWaitlist} />
          ) : undefined;
        return (
          <Sheet
            key={id}
            id={id}
            isFront={isFront}
            transform={slotTransform(geometry)}
            zIndex={geometry.z}
            className={className}
            marker={marker}
            onPick={bring}
          >
            {id === "features" ? (
              <div
                id="typora-feature-board"
                className="features-body"
                role={isFront ? "region" : undefined}
                aria-label="Typora feature requests"
              >
                {contents.features}
              </div>
            ) : (
              contents[id]
            )}
          </Sheet>
        );
      })}

      <Keyboard {...layout.stationery.keyboard} />
      <Polaroid {...layout.stationery.polaroid} />
      <StickyNote {...layout.stationery.sticky} />
      <Pencil {...layout.stationery.pencil} />
      <PaperClip {...layout.stationery.clip} />
    </main>
  );
}

/* ------------------------------------------------------------------ */
/* Single sheet (narrow viewports): the letter, board slides over it   */
/* ------------------------------------------------------------------ */

function SingleSheet({ letterRefs, form, subscribed }: StageProps) {
  const [featureBoardOpen, setFeatureBoardOpen] = useState(false);
  /** Stays true once opened so the iframe is not reloaded on every toggle. */
  const [featureBoardMounted, setFeatureBoardMounted] = useState(false);
  const close = useCallback(() => setFeatureBoardOpen(false), []);

  useEffect(() => {
    if (!featureBoardOpen) return;
    function closeOnEscape(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [close, featureBoardOpen]);

  // The board sits below the header, so the header has to be where it says it
  // is: go back to the top and hold the page there while the board is open.
  useEffect(() => {
    if (!featureBoardOpen) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [featureBoardOpen]);

  return (
    <main
      className={`stage stage-single${featureBoardOpen ? " has-feature-board" : ""}${
        subscribed ? " is-subscribed" : ""
      }`}
    >
      <header className="site-header">
        <a className="site-brand" href="/" aria-label="Typora 2.0 preview">
          <img
            className="site-logo"
            src="/typora-icon.png"
            width={40}
            height={40}
            alt=""
            decoding="async"
          />
          <span className="site-name">Typora</span>
          <span className="version-pill">2.0 preview</span>
        </a>
        <a
          className="current-version"
          href="https://typora.io"
          target="_blank"
          rel="noreferrer"
        >
          Current Version <span aria-hidden="true">↗</span>
        </a>
      </header>

      <div className="letter-stage">
        <section className="letter" aria-label="Typora 2.0 waitlist">
          <LetterContent
            refs={letterRefs}
            {...form}
            featureBoardOpen={featureBoardOpen}
            onRequestFeature={() => {
              setFeatureBoardMounted(true);
              setFeatureBoardOpen(true);
            }}
          />
        </section>
      </div>

      {featureBoardMounted ? (
        <div
          className={`feature-paper-stage${featureBoardOpen ? " is-open" : ""}`}
          inert={!featureBoardOpen}
        >
          <div
            id="typora-feature-board"
            className="feature-paper-shell"
            role="dialog"
            aria-label="Typora feature requests"
          >
            <section className="feature-paper">
              <FeatureBoardFrame active={featureBoardOpen} onRequestClose={close} />
            </section>
          </div>
        </div>
      ) : null}
    </main>
  );
}

/* ------------------------------------------------------------------ */
/* App                                                                 */
/* ------------------------------------------------------------------ */

export function App() {
  const isDesk = useMediaQuery(DESK_MEDIA);
  const titleRef = useRef<HTMLSpanElement>(null);
  const body1Ref = useRef<HTMLParagraphElement>(null);
  const body2Ref = useRef<HTMLParagraphElement>(null);
  const closingRef = useRef<HTMLParagraphElement>(null);
  const waitlistRef = useRef<HTMLDivElement>(null);
  const letterRefs: LetterRefs = {
    title: titleRef,
    body1: body1Ref,
    body2: body2Ref,
    closing: closingRef,
    waitlist: waitlistRef,
  };
  const heroRef = useRef<HTMLDivElement>(null);
  const heroHeadRef = useRef<HTMLSpanElement>(null);
  const heroMarkRef = useRef<HTMLSpanElement>(null);
  const heroRefs: HeroRefs = {
    root: heroRef,
    head: heroHeadRef,
    mark: heroMarkRef,
  };

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<WaitlistStatus>(() =>
    !CHINA_MIRROR &&
    (new URLSearchParams(window.location.search).has("pass") || readStoredSubscription())
      ? "success"
      : "idle",
  );
  const [message, setMessage] = useState(() =>
    status === "success" ? WAITLIST_SUCCESS_MESSAGE : "",
  );
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    const container = turnstileRef.current;
    if (!container || CHINA_MIRROR) return;
    let cancelled = false;

    void whenTurnstileReady().then((turnstile) => {
      if (cancelled) return;
      // The widget mode is Invisible (set on the Turnstile dashboard), so this
      // renders a 0×0 container that solves silently — nothing to show.
      widgetIdRef.current =
        turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          action: "turnstile-spin-v2",
          theme: "light",
          // Tokens expire after ~5 minutes; refresh in place so a slow reader
          // still has a valid one when they hit Subscribe.
          "expired-callback": () => {
            if (widgetIdRef.current) turnstile.reset(widgetIdRef.current);
          },
        }) ?? null;
    });

    return () => {
      cancelled = true;
      const widgetId = widgetIdRef.current;
      widgetIdRef.current = null;
      if (widgetId) window.turnstile?.remove(widgetId);
    };
  }, []);

  // Re-runs when the layout switches, because the letter remounts with it. On
  // the desk the letter is one sheet among several and simply reads as written;
  // the slogan does the typing there instead.
  useEffect(() => {
    const titleEl = titleRef.current;
    const body1El = body1Ref.current;
    const body2El = body2Ref.current;
    const closingEl = closingRef.current;
    if (!titleEl || !body1El || !body2El || !closingEl) return;

    if (isDesk || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      titleEl.textContent = TITLE_TEXT;
      body1El.textContent = BODY_1;
      body2El.innerHTML = BODY_2_HTML;
      closingEl.textContent = CLOSING_LINE;
      return;
    }

    const instances: Typed[] = [];
    let cancelled = false;

    type Step = {
      el: HTMLElement;
      strings: string[];
      contentType?: "html" | null;
    };

    const steps: Step[] = [
      { el: titleEl, strings: [TITLE_TEXT], contentType: null },
      { el: body1El, strings: [BODY_1], contentType: null },
      { el: body2El, strings: [BODY_2_HTML], contentType: "html" },
      { el: closingEl, strings: [CLOSING_LINE], contentType: null },
    ];

    function runStep(index: number) {
      if (cancelled || index >= steps.length) return;
      const step = steps[index]!;
      // The caret is a CSS ::after on the element being typed, so it always sits
      // right after the last character. typed.js' own cursor is inserted as a
      // *sibling* of the target, which drops it onto its own line after a <p>.
      step.el.classList.add(CARET_CLASS);
      const typed = new Typed(step.el, {
        strings: step.strings,
        typeSpeed: TYPE_SPEED,
        startDelay: 0,
        showCursor: false,
        autoInsertCss: false,
        contentType: step.contentType ?? undefined,
        onComplete: () => {
          step.el.classList.remove(CARET_CLASS);
          // The caret only blinks once there is nothing left to type.
          if (index === steps.length - 1) step.el.classList.add(CARET_RESTING_CLASS);
          runStep(index + 1);
        },
      });
      instances.push(typed);
    }

    runStep(0);

    return () => {
      cancelled = true;
      for (const typed of instances) typed.destroy();
      for (const step of steps)
        step.el.classList.remove(CARET_CLASS, CARET_RESTING_CLASS);
    };
  }, [isDesk]);

  // The slogan is short, so it is typed at a human pace: the caret holds steady
  // while characters land, blinks once the line is done, and the highlighter
  // sweeps across the last word a beat later.
  useEffect(() => {
    const head = heroHeadRef.current;
    const mark = heroMarkRef.current;
    if (!isDesk || !head || !mark) return;

    function finish() {
      head!.classList.remove(CARET_CLASS);
      mark!.classList.remove(CARET_CLASS);
      mark!.classList.add(CARET_RESTING_CLASS, MARK_CLASS);
    }

    head.textContent = "";
    mark.textContent = "";

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      head.textContent = SLOGAN_HEAD;
      mark.textContent = SLOGAN_MARK;
      finish();
      return;
    }

    let index = 0;
    head.classList.add(CARET_CLASS);
    let timer = window.setTimeout(function type() {
      if (index >= SLOGAN.length) {
        finish();
        return;
      }
      const char = SLOGAN[index]!;
      const target = index < SLOGAN_HEAD.length ? head! : mark!;
      if (target === mark && !mark!.classList.contains(CARET_CLASS)) {
        head!.classList.remove(CARET_CLASS);
        mark!.classList.add(CARET_CLASS);
      }
      target.textContent += char;
      index += 1;
      timer = window.setTimeout(type, keystrokeDelay(char, SLOGAN[index]));
    }, SLOGAN_START_DELAY);

    return () => {
      window.clearTimeout(timer);
      head.classList.remove(CARET_CLASS);
      mark.classList.remove(CARET_CLASS, CARET_RESTING_CLASS, MARK_CLASS);
    };
  }, [isDesk]);

  /** The widget is invisible, so a missing token means "not solved yet" — wait for it. */
  async function waitForTurnstileToken(timeoutMs = 8000): Promise<string> {
    const deadline = performance.now() + timeoutMs;
    for (;;) {
      const widgetId = widgetIdRef.current;
      const token = widgetId ? window.turnstile?.getResponse(widgetId) : undefined;
      if (token) return token;
      if (performance.now() >= deadline) return "";
      await new Promise((resolve) => window.setTimeout(resolve, 120));
    }
  }

  function resetTurnstile() {
    const widgetId = widgetIdRef.current;
    if (widgetId) window.turnstile?.reset(widgetId);
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus("error");
      setMessage("Please enter a valid email.");
      return;
    }

    setStatus("loading");
    setMessage("");

    const token = await waitForTurnstileToken();
    if (!token) {
      setStatus("error");
      setMessage("Please complete the verification challenge.");
      return;
    }

    try {
      const response = await fetch(WAITLIST_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          source: "v2.typora.io",
          "cf-turnstile-response": token,
        }),
      });
      if (!response.ok) throw new Error("Request failed");
      setStatus("success");
      setMessage(WAITLIST_SUCCESS_MESSAGE);
      setEmail("");
      storeSubscription();
      resetTurnstile();
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again.");
      resetTurnstile();
    }
  }

  const form: WaitlistProps = {
    idPrefix: "waitlist",
    email,
    status,
    message,
    onEmailChange: (value) => {
      setEmail(value);
      if (status !== "idle" && status !== "loading") {
        setStatus("idle");
        setMessage("");
      }
    },
    onSubmit,
  };

  const subscribed = status === "success";

  return (
    <>
      {isDesk ? (
        <Desk
          letterRefs={letterRefs}
          heroRefs={heroRefs}
          form={form}
          subscribed={subscribed}
        />
      ) : (
        <SingleSheet letterRefs={letterRefs} form={form} subscribed={subscribed} />
      )}
      <div ref={turnstileRef} className="turnstile-slot" />
    </>
  );
}
